import axios from "../axios/axios";
import { CnCOrderResponse } from "../types/api";
import {
  BranchServiceItem,
  CncOrderDeliveryType,
  ConsultCnCOrderRequest,
  ConsultOrderPrefillResponse,
  DeliveryTimeSlotsResponse,
  ItemInMarketplaceCountResponse,
} from "../types/consultOrder.types";
import { STANDARD_DELIVERY_DAYS } from "../utils/consultOrder.constants";

// ITAM-2583 consult-order service, ported for the ITAM-2887 advisor portal.
// Every call goes through the shared axios instance (bearer, baseURL and
// Accept-Language: SR from its request interceptor), with relative paths.
// This branch has no 401/403 logout interceptor, so an expected 403 on a
// delivery pre-check just rejects and the caller degrades. If such an
// interceptor ever lands here, the three delivery calls below move to a bare
// instance in the same change.

/**
 * GET prefill. Returns the payload on 200, or `null` on 404 (a normal
 * "not both joined yet / consult ended" state, NOT an error). Any other status
 * rejects.
 */
export const getConsultPrefill = (
  consultantId: number,
  signal?: AbortSignal,
): Promise<ConsultOrderPrefillResponse | null> => {
  return axios
    .get<ConsultOrderPrefillResponse>(
      `/click_and_collect_order/online_consult/prefill/consultant_id/${consultantId}`,
      { signal },
    )
    .then((res) => res.data)
    .catch((error) => {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    });
};

/**
 * POST create. Returns the full CnCOrderResponse (201 body). The pharmacy is
 * carried in the body (`cnCOrderRequest.marketplaceId/branchMarketplaceId`).
 * The browser sends its own User-Agent automatically; we never set that
 * header from JS.
 */
export const createConsultOrder = (
  body: ConsultCnCOrderRequest,
  signal?: AbortSignal,
): Promise<CnCOrderResponse> => {
  return axios
    .post<CnCOrderResponse>("/click_and_collect_order/online_consult", body, {
      signal,
    })
    .then((res) => res.data);
};

/**
 * GET the branch SERVICE items (delivery-fee items) of the picked pharmacy.
 * `numOfReturns` is sent large so the two fee items (standard/fast, classified
 * by their itemLowestPrice band) can never be paginated off the first page
 * (the backend default page size is only 16). An empty branch returns 404
 * (backend convention) and resolves to `[]`; any other failure rejects.
 */
export const getBranchServiceItems = (
  marketplaceId: number,
  branchMarketplaceId: number,
  signal?: AbortSignal,
): Promise<BranchServiceItem[]> => {
  return axios
    .get<ItemInMarketplaceCountResponse>(
      `/item_in_marketplace/items/${branchMarketplaceId}/${marketplaceId}/0`,
      {
        params: { filter: "item.type:'SERVICE'", numOfReturns: 1000 },
        signal,
      },
    )
    .then((res) => res.data?.list ?? [])
    .catch((error) => {
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    });
};

export interface DeliveryDistanceParams {
  lat: number;
  lng: number;
  marketplaceLat: number;
  marketplaceLng: number;
  deliveryType: CncOrderDeliveryType;
}

/**
 * GET check_delivery_distance -> boolean (address within the zone for the
 * type, computed from the picked pharmacy's coordinates). Rejects on a 403
 * (the endpoint admits the advisor only through APOTEKA_USER).
 */
export const checkDeliveryDistance = (
  params: DeliveryDistanceParams,
  signal?: AbortSignal,
): Promise<boolean> => {
  const { lat, lng, marketplaceLat, marketplaceLng, deliveryType } = params;
  return axios
    .get<boolean>(
      `/click_and_collect_order/check_delivery_distance/${lat}/${lng}/${marketplaceLat}/${marketplaceLng}`,
      { params: { deliveryType }, signal },
    )
    .then((res) => res.data);
};

export interface DeliveryTimeSlotsParams {
  lat: number;
  lng: number;
  marketplaceLat: number;
  marketplaceLng: number;
  standardDeliveryDays?: number;
}

/**
 * GET check_delivery_time_slots -> DeliveryTimeSlotsResponse. The day keys
 * are localized by the backend through Accept-Language, which the shared
 * instance pins to SR. Rejects on a 403 like the distance check.
 */
export const checkDeliveryTimeSlots = (
  params: DeliveryTimeSlotsParams,
  signal?: AbortSignal,
): Promise<DeliveryTimeSlotsResponse> => {
  const {
    lat,
    lng,
    marketplaceLat,
    marketplaceLng,
    standardDeliveryDays = STANDARD_DELIVERY_DAYS,
  } = params;
  return axios
    .get<DeliveryTimeSlotsResponse>(
      "/click_and_collect_order/check_delivery_time_slots",
      {
        params: {
          latitude: lat,
          longitude: lng,
          marketplaceLatitude: marketplaceLat,
          marketplaceLongitude: marketplaceLng,
          standardDeliveryDays,
        },
        signal,
      },
    )
    .then((res) => res.data);
};
